import { useMemo, useState, useEffect, useCallback } from "react";
import L from "leaflet";
import React from "react";
import { format } from "date-fns";
import { Namespace, useT } from "@racoon/i18n";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@racoon/ui";
import { formatTimestampToDate } from "@racoon/lib";
import { ConnectProto } from "@racoon/api";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { SuspiciousSiteMap, MOCK_GEO_DATA } from "./suspicious-site-map";
import { EditFieldModal } from "../../../shared/components/edit-field-modal";
import { useCanEditGovDash } from "~/shared/hooks/useCanEditGovDash";
import {
  useUpdateSuspiciousLocationMutation,
  useArchiveSuspiciousLocationMutation,
  useUnarchiveSuspiciousLocationMutation,
  useGetGovSuspiciousLocationAnalyticsQuery,
} from "~/shared/api/govDash/govDash.api";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import { DateView, getThisWeekDates } from "./dateView";
import { ViewSelector } from "../../../shared/components/viewSelector";
import { convertGeoJsonToLeaflet, convertLeafletToGeoJson } from "../utils/coordinateConversion";
type SuspiciousSiteSection = "general" | "zone" | "anomalies";

export const SuspiciousSitesDetails = ({ selectedRecord }: { selectedRecord?: any }) => {
  const t = useT();
  const [activeSection, setActiveSection] = useState<SuspiciousSiteSection>("general");
  const [isEditingGeofence, setIsEditingGeofence] = useState(false);
  const canEdit = useCanEditGovDash();
  const [updateSuspiciousLocation] = useUpdateSuspiciousLocationMutation();
  const [archiveSuspiciousLocation, { isLoading: isArchiving }] =
    useArchiveSuspiciousLocationMutation();
  const [unarchiveSuspiciousLocation, { isLoading: isUnarchiving }] =
    useUnarchiveSuspiciousLocationMutation();
  const isArchived = selectedRecord?.location?.status === ConnectProto.LocationStatus.INACTIVE;

  // Backend keeps pre-archive anomalies visible; a denied role surfaces via the
  // global error toast, so no extra permission handling here.
  const handleArchiveToggle = useCallback(async () => {
    const locationId = selectedRecord?.location?.id;
    if (locationId == null || isArchiving || isUnarchiving) return;
    try {
      const toggle = isArchived ? unarchiveSuspiciousLocation : archiveSuspiciousLocation;
      await toggle({ id: locationId }).unwrap();
    } catch (error) {
      console.error("Error toggling suspicious location archive state:", error);
    }
  }, [
    selectedRecord,
    isArchived,
    isArchiving,
    isUnarchiving,
    archiveSuspiciousLocation,
    unarchiveSuspiciousLocation,
  ]);
  // Use real data from selectedRecord if available
  const realGeofenceCoordinates = useMemo(() => {
    if (!selectedRecord?.location?.geometry?.coordinates?.[0]) {
      return MOCK_GEO_DATA.boundaryCoordinates;
    }
    return convertGeoJsonToLeaflet(selectedRecord.location.geometry.coordinates[0]);
  }, [selectedRecord]);

  const [geofenceCoordinates, setGeofenceCoordinates] =
    useState<[number, number][]>(realGeofenceCoordinates);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [editValue, setEditValue] = useState<string>("");

  // Format date from API
  const formatCreatedDate = (record: any): string => {
    if (!record?.createdAt) return "Неизвестно";
    try {
      let date: Date;
      if (typeof record.createdAt === "object" && record.createdAt.seconds) {
        const seconds = parseInt(record.createdAt.seconds);
        date = new Date(seconds * 1000);
      } else {
        date = new Date(record.createdAt);
      }
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("ru-RU", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
    } catch (error) {
      console.error("Error parsing createdAt:", error);
    }
    return "Неизвестно";
  };

  // Sync geofenceCoordinates with realGeofenceCoordinates when selectedRecord changes
  useEffect(() => {
    setGeofenceCoordinates(realGeofenceCoordinates);
  }, [realGeofenceCoordinates]);
  const featureGroupRef = React.useRef<L.FeatureGroup>(null);

  // Get last week's dates (one week ago) - as state to support date changes
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const { weekStart: thisWeekStart } = getThisWeekDates();
    const start = new Date(thisWeekStart);
    start.setDate(start.getDate() - 7);
    return start;
  });

  const [weekEnd, setWeekEnd] = useState<Date>(() => {
    const { weekEnd: thisWeekEnd } = getThisWeekDates();
    return new Date(thisWeekEnd);
  });

  // Handle date changes from DateView component
  const handleDateChange = useCallback((newWeekStart: Date, newWeekEnd: Date) => {
    setWeekStart(newWeekStart);
    setWeekEnd(newWeekEnd);
  }, []);

  // Fetch analytics data for the selected week and location
  const { data: analyticsData } = useGetGovSuspiciousLocationAnalyticsQuery(
    {
      suspiciousLocationId: selectedRecord?.id || 0,
      fromTo: {
        from: {
          seconds: BigInt(Math.floor(weekStart.getTime() / 1000)),
          nanos: 0,
        },
        to: {
          seconds: BigInt(Math.floor(weekEnd.getTime() / 1000)),
          nanos: 0,
        },
      },
    },
    {
      skip: !selectedRecord?.id,
    },
  );

  // Handle geofence save or edit mode toggle
  const handleGeofenceSaveOrEdit = useCallback(async () => {
    if (isEditingGeofence && selectedRecord) {
      // Save geofence coordinates to API
      try {
        // Convert from [lat, lon] to [lon, lat] for GeoJSON
        const geoJsonCoordinates = convertLeafletToGeoJson(geofenceCoordinates);

        const updatePayload: any = {
          id: selectedRecord.id,
          geometry: {
            type: "Polygon",
            coordinates: [geoJsonCoordinates],
          },
        };
        const result = await updateSuspiciousLocation(updatePayload).unwrap();

        // Update realGeofenceCoordinates by updating selectedRecord reference
        // This ensures the map stays in sync after successful update
        if (result?.geometry?.coordinates && Array.isArray(result.geometry.coordinates)) {
          const coords = result.geometry.coordinates[0];
          if (Array.isArray(coords)) {
            const convertedCoords = convertGeoJsonToLeaflet(coords as any);
            setGeofenceCoordinates(convertedCoords);
          }
        }

        // Exit edit mode after successful update
        setIsEditingGeofence(false);
      } catch (error) {
        console.error("Error updating geofence:", error);
      }
    } else {
      // Enter edit mode
      setIsEditingGeofence(true);
    }
  }, [isEditingGeofence, selectedRecord, geofenceCoordinates, updateSuspiciousLocation]);

  const suspiciousViewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        { value: "zone" as const, label: t(Namespace.Govdash, "recordViewZone") },
        { value: "anomalies" as const, label: t(Namespace.Govdash, "recordViewAnomaliesTab") },
      ] as const,
    [t],
  );

  const handleEditClick = (fieldKey: string, currentValue: string) => {
    setEditingField(fieldKey);
    setEditValue(currentValue);
  };

  const handleSave = async () => {
    if (editingField && selectedRecord) {
      try {
        // Prepare update payload with only changed field
        const updatePayload: any = {
          id: selectedRecord.id,
        };

        // Add only the field that was edited
        if (editingField === "name") {
          updatePayload.name = editValue;
        } else if (editingField === "address") {
          updatePayload.address = editValue;
        } else if (editingField === "event") {
          updatePayload.event = editValue;
        }

        // Send update request to API
        await updateSuspiciousLocation(updatePayload).unwrap();

        setEditingField(null);
        setEditValue("");
      } catch (error) {
        console.error("Error updating suspicious location:", error);
      }
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue("");
  };

  const generalRows = useMemo(() => {
    if (!selectedRecord) return [];
    const rows = [
      {
        label: t(Namespace.Customers, "table.name"),
        value: selectedRecord.title || selectedRecord.name || "Неизвестная локация",
        key: "name",
        editable: canEdit,
      },
      {
        label: t(Namespace.Govdash, "status.address"),
        value: selectedRecord.location.address || "-",
        shouldWrap: true,
        key: "address",
        editable: canEdit,
      },
      {
        label: t(Namespace.Govdash, "suspiciousSites.event"),
        value: selectedRecord.event || "Остановка",
        key: "event",
        editable: canEdit,
      },
      {
        label: t(Namespace.Govdash, "status.createdAt"),
        value: formatCreatedDate(selectedRecord.location),
        key: "createdDate",
        editable: false,
      },
      {
        label: t(Namespace.Govdash, "anomaliesDetectedInGivenPeriod"),
        value: selectedRecord.totalStops.toString(),
        highlight: true,
        key: "anomaliesCount",
        editable: false,
      },
    ];
    if (selectedRecord.archivedAt?.seconds != null) {
      rows.push({
        label: t(Namespace.Govdash, "suspiciousSites.archivedAt"),
        value: formatTimestampToDate(selectedRecord.archivedAt),
        key: "archivedAt",
        editable: false,
      });
    }
    return rows;
  }, [selectedRecord, canEdit, t]);

  return (
    <>
      {/* Archive / restore confirmation */}
      <Dialog open={isArchiveConfirmOpen} onOpenChange={setIsArchiveConfirmOpen}>
        <DialogContent>
          <DialogTitle>
            {isArchived
              ? t(Namespace.Govdash, "suspiciousSites.restoreConfirmTitle")
              : t(Namespace.Govdash, "suspiciousSites.archiveConfirmTitle")}
          </DialogTitle>
          <DialogDescription>
            {isArchived
              ? t(Namespace.Govdash, "suspiciousSites.restoreConfirmDescription")
              : t(Namespace.Govdash, "suspiciousSites.archiveConfirmDescription")}
          </DialogDescription>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsArchiveConfirmOpen(false)}>
              {t(Namespace.Common, "cancel")}
            </Button>
            <Button
              type="button"
              variant={isArchived ? "solidGreen" : "solidRed"}
              disabled={isArchiving || isUnarchiving}
              onClick={() => {
                setIsArchiveConfirmOpen(false);
                void handleArchiveToggle();
              }}
            >
              {isArchived
                ? t(Namespace.Buttons, "restore")
                : t(Namespace.Govdash, "suspiciousSites.archive")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <EditFieldModal
        isOpen={!!editingField}
        fieldLabel={generalRows.find((r) => r.key === editingField)?.label || ""}
        value={editValue}
        onValueChange={setEditValue}
        onCancel={handleCancel}
        onSave={handleSave}
      />

      {/* Main Content */}
      <CardContentWrapper className="h-full flex flex-col" hasDivider={false}>
        <div className="p-2 flex-shrink-0">
          <ViewSelector
            options={suspiciousViewOptions}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            compact
          />
        </div>

        {activeSection === "general" && (
          <div className="p-4 flex-1 overflow-y-auto">
            {selectedRecord ? (
              <>
                <CardContentWrapper>
                  {generalRows.map((row) => (
                    <div key={row.label}>
                      {row.highlight ? (
                        <div
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={() => row.editable && handleEditClick(row.key, row.value)}
                        >
                          <p className="text-sm font-normal leading-[14px] tracking-tight text-text-secondary">
                            {row.label}
                          </p>
                          <p
                            className={`text-sm font-normal leading-[14px] tracking-tight ${
                              row.key === "anomaliesCount"
                                ? parseInt(row.value) > 0
                                  ? "text-brand-red cursor-pointer"
                                  : "text-brand-green cursor-pointer"
                                : row.editable
                                  ? "text-brand-blue cursor-pointer"
                                  : "text-brand-red"
                            }`}
                          >
                            {row.value}
                          </p>
                        </div>
                      ) : (
                        <div
                          className={`cursor-pointer rounded-lg transition-colors ${row.editable ? "hover:bg-gray-50" : ""}`}
                          onClick={() => row.editable && handleEditClick(row.key, row.value)}
                        >
                          <div className="flex items-center justify-between p-4 flex-wrap gap-2">
                            <p className="text-sm font-normal leading-[14px] tracking-tight text-text-secondary flex-1 text-left">
                              {row.label}
                            </p>
                            <p
                              className={`text-sm font-normal leading-[14px] tracking-tight flex-1 text-right ${row.editable ? "text-brand-blue cursor-pointer" : "text-text-primary"}`}
                            >
                              {row.value}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContentWrapper>
                {canEdit && (
                  <Button
                    type="button"
                    variant={isArchived ? "solidGreen" : "opacityRedWithOutline"}
                    className="mt-4 w-full"
                    disabled={isArchiving || isUnarchiving}
                    onClick={() => setIsArchiveConfirmOpen(true)}
                  >
                    {isArchived
                      ? t(Namespace.Buttons, "restore")
                      : t(Namespace.Govdash, "suspiciousSites.archive")}
                  </Button>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-text-secondary text-sm">{t(Namespace.Common, "noData")}</p>
              </div>
            )}
          </div>
        )}

        {activeSection === "zone" && (
          <div className="flex-1 flex flex-col min-h-0 p-2">
            <div className="flex-1 relative min-h-0">
              <SuspiciousSiteMap
                isEditingGeofence={isEditingGeofence}
                boundaryCoordinates={geofenceCoordinates}
                onGeofenceUpdate={setGeofenceCoordinates}
                featureGroupRef={featureGroupRef}
              />
              {canEdit && (
                <button
                  type="button"
                  onClick={handleGeofenceSaveOrEdit}
                  className={`absolute bottom-6 left-1/2 transform -translate-x-1/2 h-10 px-6 rounded-lg font-semibold text-base leading-4 tracking-tight transition-colors z-[1000] ${
                    isEditingGeofence
                      ? "bg-green-500 text-white hover:bg-green-600"
                      : "bg-brand-blue text-white hover:bg-blue-700"
                  }`}
                >
                  {isEditingGeofence
                    ? t(Namespace.Govdash, "actions.save")
                    : t(Namespace.Govdash, "actions.edit")}
                </button>
              )}
            </div>
          </div>
        )}

        {activeSection === "anomalies" && (
          <div className="flex-1 flex flex-col min-h-0 space-y-2">
            <DateView weekStart={weekStart} weekEnd={weekEnd} onDateChange={handleDateChange} />

            {analyticsData?.gpsStops && analyticsData.gpsStops.length > 0 ? (
              <div className="flex-1 relative min-h-0 overflow-hidden">
                <div className="absolute inset-0 overflow-y-auto space-y-0">
                  {analyticsData.gpsStops.map((item: any, index: number) => {
                    const displayItem = item.truck
                      ? {
                          id: item.id,
                          licensePlate: item.truck?.plateNumber || "N/A",
                          organization: item.ownerOrganization?.name || "N/A",
                          time: format(new Date(Number(item.gpsStartAt.seconds) * 1000), "HH:mm"),
                          date: format(
                            new Date(Number(item.gpsStartAt.seconds) * 1000),
                            "dd.MM.yyyy",
                          ),
                          reason: "стоянка",
                          duration: `${Math.floor((Number(item?.gpsEndAt?.seconds) - Number(item?.gpsStartAt?.seconds)) / 60)}м ${(Number(item.gpsEndAt.seconds) - Number(item.gpsStartAt.seconds)) % 60}с`,
                        }
                      : item;

                    const listLength = analyticsData.gpsStops.length;
                    return (
                      <div
                        key={displayItem.id}
                        className={`flex p-4 gap-4 ${
                          index === 0 ? "border-t" : ""
                        } ${index === listLength - 1 ? "" : "border-b"}`}
                      >
                        <div className="text-center items-center flex">
                          <span className="font-bold text-xs uppercase text-slate-900">
                            {displayItem.id}
                          </span>
                        </div>
                        <div className="flex flex-col flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <div className="border border-slate-900 rounded px-1">
                                <p className="text-xs font-medium text-slate-900">
                                  {displayItem.licensePlate}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm font-normal text-text-primary">
                              {displayItem.organization}
                            </p>
                          </div>

                          <div className="flex items-center justify-between gap-2 text-xs">
                            <div className="flex gap-2 items-center text-text-secondary">
                              <span>{displayItem.time}</span>
                              <span>{displayItem.date}</span>
                            </div>
                            <div className="flex gap-2 items-center text-slate-600">
                              <span className="uppercase text-brand-red font-bold">
                                {displayItem.reason}
                              </span>
                              <span className="text-brand-red">{displayItem.duration}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-text-secondary text-sm">{t(Namespace.Common, "noData")}</p>
              </div>
            )}
          </div>
        )}
      </CardContentWrapper>
    </>
  );
};
